import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client'; // Pour futures mises à jour
import { UserRole } from '@/types';
import { useTranslation } from 'react-i18next';

export interface VerificationRequest {
  id: string;
  userId: string;
  displayName?: string;
  role?: UserRole;
  documentType?: string;
  documentImage?: string; // URL ou path
  selfieImage?: string; // URL ou path
  submittedAt: Date;
  status: 'pending' | 'approved' | 'rejected';
}

const MOCK_VERIFICATION_REQUESTS: VerificationRequest[] = [
  {
    id: '1',
    userId: '103',
    displayName: 'Emma Laurent',
    role: 'escort',
    documentType: 'ID Card',
    documentImage: 'id_verification.jpg',
    selfieImage: 'selfie_verification.jpg',
    submittedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1),
    status: 'pending',
  },
  {
    id: '2',
    userId: '108',
    displayName: 'Ana Barcelona',
    role: 'escort',
    documentType: 'Passport',
    documentImage: 'passport_verification.jpg',
    selfieImage: 'selfie_verification.jpg',
    submittedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3),
    status: 'pending',
  },
  {
    id: '3',
    userId: '203',
    displayName: 'Robert K.',
    role: 'client',
    documentType: 'Driver License',
    documentImage: 'license_verification.jpg',
    selfieImage: 'selfie_verification.jpg',
    submittedAt: new Date(Date.now() - 1000 * 60 * 60 * 36),
    status: 'pending',
  },
];

export const useVerificationRequests = () => {
  const [requests, setRequests] = useState<VerificationRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { t } = useTranslation();

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // TODO: Remplacer par appel API réel
      await new Promise(resolve => setTimeout(resolve, 700)); // Simuler délai
      setRequests(MOCK_VERIFICATION_REQUESTS);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch verification requests';
      console.error('Error fetching requests:', err);
      setError(errorMessage);
      toast({ title: t('common.error') || 'Error', description: t('admin.error_loading_verifications') || errorMessage, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const updateVerificationStatus = useCallback(async (requestId: string, newStatus: 'approved' | 'rejected') => {
    // TODO: Implémenter l'appel API réel + mise à jour du statut vérifié de l'utilisateur
    console.log(`Updating verification request ${requestId} status to ${newStatus}`);
    setRequests(currentRequests =>
      currentRequests.map(req =>
        req.id === requestId ? { ...req, status: newStatus } : req
      )
    );
    toast({ 
      title: t('common.success') || 'Success', 
      description: t('admin.verification_status_updated', { requestId, status: newStatus }) || `Verification request ${requestId} status updated to ${newStatus}.` 
    });
    return true; // Simuler succès
  }, [toast, t]);

  return {
    requests,
    isLoading,
    error,
    fetchRequests,
    updateVerificationStatus,
  };
}; 